/**
 * Rich Text Editor with Checklist Support - Extended Version
 * TipTap-based editor with full feature set
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Fragment } from 'prosemirror-model';
import StarterKit from '@tiptap/starter-kit';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import TiptapImage from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import FontFamily from '@tiptap/extension-font-family';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import Highlight from '@tiptap/extension-highlight';
import Strike from '@tiptap/extension-strike';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  CheckSquare,
  Undo,
  Redo,
  Heading1,
  Heading2,
  Heading3,
  Minus,
  AArrowUp,
  AArrowDown,
  Link as LinkIcon,
  Unlink,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Palette,
  Highlighter,
  Quote,
  Code,
  Image,
  PlusSquare,
  TableIcon,
  Trash2,
  Maximize2,
  X,
  Superscript as SuperscriptIcon,
  Subscript as SubscriptIcon,
  PencilLine,
  Paintbrush,
  RemoveFormatting,
  IndentIncrease,
  IndentDecrease,
  ArrowUpDown,
  Search,
  Replace,
  PaintBucket,
  Square,
  Copy,
  Scissors,
  ClipboardPaste,
  Eraser,
  Undo2,
  Circle,
  Type,
} from 'lucide-react';

// Canvas drawing colors
const CANVAS_COLORS = [
  "#000000", "#ef4444", "#f97316", "#eab308", 
  "#22c55e", "#3b82f6", "#8b5cf6", "#ec4899"
];

// ========== PHASE 1: NORMALIZED STROKE DATA MODEL ==========

// Single stroke point (normalized 0-1 coordinates)
export interface CanvasStrokePoint {
  x: number; // 0-1 normalized
  y: number; // 0-1 normalized
}

// A single stroke (pen/eraser)
export interface CanvasStroke {
  points: CanvasStrokePoint[];
  color: string;
  width: number; // CSS pixels (visual thickness)
  isEraser: boolean;
}

// Canvas text field interface
export interface CanvasTextField {
  id: string;
  x: number; // Normalized 0-1
  y: number; // Normalized 0-1
  text: string;
  fontSize: number;
  color: string;
  width: number; // Normalized 0-1
  height?: number; // Normalized 0-1
}

// Canvas data structure (strokes + text fields) - NO legacy imageData
export interface CanvasData {
  strokes: CanvasStroke[];
  textFields: CanvasTextField[];
}

// Helper to parse drawing data (new normalized stroke format)
function parseCanvasData(data: string): CanvasData {
  if (!data) return { strokes: [], textFields: [] };
  
  // Try to parse as JSON
  if (data.startsWith('{')) {
    try {
      const parsed = JSON.parse(data);
      return {
        strokes: Array.isArray(parsed.strokes) ? parsed.strokes : [],
        textFields: Array.isArray(parsed.textFields) ? parsed.textFields : [],
      };
    } catch {
      return { strokes: [], textFields: [] };
    }
  }
  
  // If it's a data URL (legacy), we ignore it - fresh start
  return { strokes: [], textFields: [] };
}

// Serialize canvas data to JSON string
function serializeCanvasData(data: CanvasData): string {
  return JSON.stringify({
    strokes: data.strokes,
    textFields: data.textFields,
  });
}

interface RichTextEditorChecklistProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
  showProgress?: boolean;
  showWordCount?: boolean;
  editable?: boolean;
  // Canvas integration (optional)
  enableCanvas?: boolean;
  drawingData?: string;
  onDrawingChange?: (drawing: string) => void;
}

// Custom TaskItem that allows nested lists
const CustomTaskItem = TaskItem.extend({
  content: 'paragraph block*',
});

// Custom extension for font size
const FontSize = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: element => element.style.fontSize?.replace('px', '') || null,
        renderHTML: attributes => {
          if (!attributes.fontSize) return {};
          return { style: `font-size: ${attributes.fontSize}px` };
        },
      },
    };
  },
});

// Font sizes - numeric values
const FONT_SIZES = ['8', '9', '10', '11', '12', '14', '16', '18', '20', '22', '24', '26', '28', '36', '48', '72'];

// Font families
const FONT_FAMILIES = [
  { label: 'Standard', value: '' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Times New Roman', value: 'Times New Roman, serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Courier New', value: 'Courier New, monospace' },
  { label: 'Trebuchet MS', value: 'Trebuchet MS, sans-serif' },
  { label: 'Comic Sans MS', value: 'Comic Sans MS, cursive' },
  { label: 'Tahoma', value: 'Tahoma, sans-serif' },
  { label: 'Palatino', value: 'Palatino Linotype, serif' },
  { label: 'Garamond', value: 'Garamond, serif' },
  { label: 'Impact', value: 'Impact, sans-serif' },
  { label: 'Lucida Console', value: 'Lucida Console, monospace' },
];

// Simple toolbar button
function ToolbarBtn({ 
  onClick, 
  isActive = false, 
  disabled = false,
  children,
  title,
}: { 
  onClick: () => void; 
  isActive?: boolean; 
  disabled?: boolean;
  children: React.ReactNode;
  title: string;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'h-8 w-8 p-0 flex-shrink-0',
        isActive && 'bg-muted text-primary'
      )}
    >
      {children}
    </Button>
  );
}

// Toolbar divider
function Divider() {
  return <div className="w-px h-6 bg-border mx-0.5 flex-shrink-0" />;
}

// Link input dialog with Ctrl+K support
function LinkDialog({ 
  onSubmit, 
  onRemove,
  initialUrl = '',
  hasLink = false,
  isOpen,
  onOpenChange,
}: { 
  onSubmit: (url: string) => void;
  onRemove: () => void;
  initialUrl?: string;
  hasLink?: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [url, setUrl] = useState(initialUrl);

  useEffect(() => {
    if (isOpen) {
      setUrl(initialUrl);
    }
  }, [isOpen, initialUrl]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (url.trim()) {
      onSubmit(url.trim().startsWith('http') ? url.trim() : `https://${url.trim()}`);
      onOpenChange(false);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          title="Link (Strg+K)"
          className={cn('h-8 w-8 p-0 flex-shrink-0', hasLink && 'text-primary')}
        >
          <LinkIcon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3 z-[10000]" align="start">
        <form onSubmit={handleSubmit} className="space-y-2">
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://beispiel.de"
            className="h-8 text-sm"
            autoFocus
          />
          <div className="flex gap-2">
            <Button type="submit" size="sm" className="flex-1">
              Link einfügen
            </Button>
            {hasLink && (
              <Button 
                type="button" 
                variant="destructive" 
                size="sm"
                onClick={() => {
                  onRemove();
                  onOpenChange(false);
                }}
              >
                <Unlink className="h-4 w-4" />
              </Button>
            )}
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}

// Font family picker
function FontFamilyPicker({ 
  currentFamily, 
  onSelect 
}: {
  currentFamily: string | null;
  onSelect: (family: string | null) => void;
}) {
  const displayName = FONT_FAMILIES.find(f => f.value === currentFamily)?.label || 'Standard';
  
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 px-2 flex-shrink-0 text-xs min-w-[100px] justify-between font-normal"
          title="Schriftart"
        >
          <span className="truncate">{displayName}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1 z-[10000]" align="start">
        <div className="max-h-60 overflow-y-auto overscroll-contain">
          {FONT_FAMILIES.map((font) => (
            <Button
              key={font.value || 'default'}
              variant="ghost"
              size="sm"
              className={cn(
                'w-full justify-start text-sm',
                currentFamily === font.value && 'bg-muted'
              )}
              style={font.value ? { fontFamily: font.value } : undefined}
              onClick={() => onSelect(font.value || null)}
            >
              {font.label}
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Font size picker - compact dropdown style
function FontSizePicker({ 
  currentSize, 
  onSelect,
  onIncrease,
  onDecrease
}: {
  currentSize: string | null;
  onSelect: (size: string | null) => void;
  onIncrease: () => void;
  onDecrease: () => void;
}) {
  return (
    <div className="flex items-center gap-0.5">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 w-12 px-2 flex-shrink-0 text-xs font-mono justify-center"
            title="Schriftgröße"
          >
            {currentSize || '14'}
        </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2 z-[10000]" align="start">
          <div className="grid grid-cols-4 gap-1 max-h-60 overflow-y-auto overscroll-contain">
            {FONT_SIZES.map((size) => (
              <Button
                key={size}
                variant="ghost"
                size="sm"
                className={cn(
                  'h-7 w-9 p-0 font-mono text-xs',
                  currentSize === size && 'bg-primary text-primary-foreground hover:bg-primary/90'
                )}
                onClick={() => onSelect(size)}
              >
                {size}
              </Button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      <ToolbarBtn onClick={onIncrease} title="Schrift vergrößern">
        <AArrowUp className="h-4 w-4" />
      </ToolbarBtn>
      <ToolbarBtn onClick={onDecrease} title="Schrift verkleinern">
        <AArrowDown className="h-4 w-4" />
      </ToolbarBtn>
    </div>
  );
}

// Image URL dialog
function ImageDialog({ onSubmit }: { onSubmit: (url: string) => void }) {
  const [url, setUrl] = useState('');
  const [open, setOpen] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (url.trim()) {
      onSubmit(url.trim());
      setUrl('');
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 flex-shrink-0" title="Bild einfügen">
          <Image className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3 z-[10000]" align="start">
        <form onSubmit={handleSubmit} className="space-y-2">
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://beispiel.de/bild.jpg"
            className="h-8 text-sm"
            autoFocus
          />
          <Button type="submit" size="sm" className="w-full">
            Bild einfügen
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}

// Visual table grid picker (like MS Word)
function TableGridPicker({ onSelect }: { onSelect: (rows: number, cols: number) => void }) {
  const [hoverRows, setHoverRows] = useState(0);
  const [hoverCols, setHoverCols] = useState(0);
  const maxRows = 8;
  const maxCols = 8;

  return (
    <div className="p-2">
      <div className="text-xs text-muted-foreground mb-2 text-center">
        {hoverRows > 0 && hoverCols > 0 ? `${hoverRows} × ${hoverCols} Tabelle` : 'Größe wählen'}
      </div>
      <div 
        className="grid gap-0.5"
        style={{ gridTemplateColumns: `repeat(${maxCols}, 1fr)` }}
        onMouseLeave={() => { setHoverRows(0); setHoverCols(0); }}
      >
        {Array.from({ length: maxRows * maxCols }).map((_, i) => {
          const row = Math.floor(i / maxCols) + 1;
          const col = (i % maxCols) + 1;
          const isHighlighted = row <= hoverRows && col <= hoverCols;
          
          return (
            <button
              key={i}
              type="button"
              className={cn(
                'w-4 h-4 border rounded-sm transition-colors',
                isHighlighted 
                  ? 'bg-primary border-primary' 
                  : 'bg-muted/50 border-border hover:border-primary/50'
              )}
              onMouseEnter={() => { setHoverRows(row); setHoverCols(col); }}
              onClick={() => onSelect(row, col)}
            />
          );
        })}
      </div>
    </div>
  );
}

// Full color picker with custom input
function FullColorPicker({ 
  currentColor, 
  onSelect, 
  icon: Icon,
  tooltip,
  type = 'text'
}: {
  currentColor: string | null;
  onSelect: (color: string | null) => void;
  icon: React.ElementType;
  tooltip: string;
  type?: 'text' | 'highlight';
}) {
  const [customColor, setCustomColor] = useState(currentColor || '#000000');
  
  const presetColors = type === 'text' ? [
    '#000000', '#374151', '#6b7280', '#9ca3af',
    '#ef4444', '#f97316', '#f59e0b', '#eab308',
    '#84cc16', '#22c55e', '#10b981', '#14b8a6',
    '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
    '#8b5cf6', '#a855f7', '#d946ef', '#ec4899',
  ] : [
    null, // No highlight
    '#fef3c7', '#fef08a', '#fde047', '#facc15',
    '#d9f99d', '#bbf7d0', '#86efac', '#4ade80',
    '#a5f3fc', '#bfdbfe', '#93c5fd', '#60a5fa',
    '#e9d5ff', '#d8b4fe', '#c4b5fd', '#a78bfa',
    '#fbcfe8', '#f9a8d4', '#f472b6', '#ec4899',
  ];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 flex-shrink-0 relative"
          title={tooltip}
        >
          <Icon className="h-4 w-4" />
          {currentColor && (
            <span 
              className="absolute bottom-1 left-1 right-1 h-0.5 rounded-full" 
              style={{ backgroundColor: currentColor }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3 z-[10000]" align="start">
        <div className="space-y-3">
          <div className="grid grid-cols-10 gap-1">
            {presetColors.map((color, i) => (
              <button
                key={color || `no-color-${i}`}
                type="button"
                onClick={() => onSelect(color)}
                className={cn(
                  'w-5 h-5 rounded border transition-transform hover:scale-110',
                  color === currentColor && 'ring-2 ring-primary ring-offset-1',
                  !color && 'bg-white border-dashed border-gray-300 relative after:absolute after:inset-0 after:bg-[linear-gradient(45deg,transparent_45%,#ef4444_45%,#ef4444_55%,transparent_55%)]'
                )}
                style={color ? { backgroundColor: color } : undefined}
                title={color || 'Keine'}
              />
            ))}
          </div>
          <div className="flex items-center gap-2 pt-2 border-t">
            <input
              type="color"
              value={customColor}
              onChange={(e) => setCustomColor(e.target.value)}
              className="w-8 h-8 p-0 border-0 rounded cursor-pointer"
            />
            <Input
              value={customColor}
              onChange={(e) => setCustomColor(e.target.value)}
              placeholder="#000000"
              className="h-8 text-xs flex-1 font-mono"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={() => onSelect(customColor)}
            >
              OK
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Table menu with visual picker
function TableMenu({
  onInsert, 
  onAddRow, 
  onAddColumn, 
  onDeleteRow, 
  onDeleteColumn, 
  onDeleteTable,
  hasTable 
}: {
  onInsert: (rows: number, cols: number) => void;
  onAddRow: () => void;
  onAddColumn: () => void;
  onDeleteRow: () => void;
  onDeleteColumn: () => void;
  onDeleteTable: () => void;
  hasTable: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          type="button" 
          variant="ghost" 
          size="sm" 
          className={cn('h-8 w-8 p-0 flex-shrink-0', hasTable && 'text-primary')} 
          title="Tabelle"
        >
          <TableIcon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-1 z-[10000]" align="start">
        {!hasTable ? (
          <TableGridPicker 
            onSelect={(rows, cols) => {
              onInsert(rows, cols);
              setOpen(false);
            }} 
          />
        ) : (
          <div className="space-y-0.5 min-w-[160px]">
            <Button variant="ghost" size="sm" className="w-full justify-start" onClick={onAddRow}>
              Zeile hinzufügen
            </Button>
            <Button variant="ghost" size="sm" className="w-full justify-start" onClick={onAddColumn}>
              Spalte hinzufügen
            </Button>
            <div className="h-px bg-border my-1" />
            <Button variant="ghost" size="sm" className="w-full justify-start text-destructive" onClick={onDeleteRow}>
              Zeile löschen
            </Button>
            <Button variant="ghost" size="sm" className="w-full justify-start text-destructive" onClick={onDeleteColumn}>
              Spalte löschen
            </Button>
            <Button variant="ghost" size="sm" className="w-full justify-start text-destructive" onClick={onDeleteTable}>
              <Trash2 className="h-4 w-4 mr-2" />
              Tabelle löschen
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Search and Replace dialog
function SearchReplaceDialog({ 
  editor 
}: { 
  editor: any;
}) {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [matchCount, setMatchCount] = useState(0);

  const handleSearch = () => {
    if (!searchText || !editor) return;
    
    const content = editor.getText();
    const regex = new RegExp(searchText, 'gi');
    const matches = content.match(regex);
    setMatchCount(matches?.length || 0);
  };

  const handleReplace = () => {
    if (!searchText || !editor) return;
    
    const { state } = editor;
    const { from, to } = state.selection;
    const selectedText = state.doc.textBetween(from, to);
    
    if (selectedText.toLowerCase() === searchText.toLowerCase()) {
      editor.chain().focus().insertContent(replaceText).run();
    }
  };

  const handleReplaceAll = () => {
    if (!searchText || !editor) return;
    
    const html = editor.getHTML();
    const regex = new RegExp(searchText, 'gi');
    const newHtml = html.replace(regex, replaceText);
    editor.commands.setContent(newHtml);
    setMatchCount(0);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          type="button" 
          variant="ghost" 
          size="sm" 
          className="h-8 w-8 p-0 flex-shrink-0"
          title="Suchen und Ersetzen"
        >
          <Search className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3 z-[10000]" align="start">
        <div className="space-y-3">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyUp={handleSearch}
                placeholder="Suchen..."
                className="h-8 text-sm flex-1"
              />
            </div>
            <div className="flex items-center gap-2">
              <Replace className="h-4 w-4 text-muted-foreground" />
              <Input
                value={replaceText}
                onChange={(e) => setReplaceText(e.target.value)}
                placeholder="Ersetzen mit..."
                className="h-8 text-sm flex-1"
              />
            </div>
          </div>
          {matchCount > 0 && (
            <p className="text-xs text-muted-foreground">{matchCount} Treffer gefunden</p>
          )}
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" onClick={handleReplace} className="flex-1 text-xs">
              Ersetzen
            </Button>
            <Button type="button" size="sm" onClick={handleReplaceAll} className="flex-1 text-xs">
              Alle ersetzen
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Fill/Border color picker for cells
function FillBorderPicker({ 
  editor
}: {
  editor: any;
}) {
  const [fillColor, setFillColor] = useState('#ffffff');
  const [borderColor, setBorderColor] = useState('#000000');
  
  const presetColors = [
    '#ffffff', '#f3f4f6', '#e5e7eb', '#d1d5db',
    '#fef3c7', '#fef08a', '#d9f99d', '#bbf7d0',
    '#a5f3fc', '#bfdbfe', '#e9d5ff', '#fbcfe8',
  ];

  const applyFill = (color: string) => {
    setFillColor(color);
    // Apply to current selection or cell
    if (editor.isActive('tableCell') || editor.isActive('tableHeader')) {
      editor.chain().focus().setCellAttribute('backgroundColor', color).run();
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button 
          type="button" 
          variant="ghost" 
          size="sm" 
          className="h-8 w-8 p-0 flex-shrink-0 relative"
          title="Füllung & Rahmen"
        >
          <PaintBucket className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3 z-[10000]" align="start">
        <div className="space-y-3">
          <div>
            <p className="text-xs font-medium mb-2 flex items-center gap-1">
              <PaintBucket className="h-3 w-3" /> Füllung
            </p>
            <div className="grid grid-cols-6 gap-1">
              {presetColors.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => applyFill(color)}
                  className={cn(
                    'w-5 h-5 rounded border transition-transform hover:scale-110',
                    color === fillColor && 'ring-2 ring-primary ring-offset-1'
                  )}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
          <div className="border-t pt-2">
            <p className="text-xs font-medium mb-2 flex items-center gap-1">
              <Square className="h-3 w-3" /> Rahmen
            </p>
            <div className="grid grid-cols-6 gap-1">
              {['#000000', '#374151', '#6b7280', '#ef4444', '#3b82f6', '#22c55e'].map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setBorderColor(color)}
                  className={cn(
                    'w-5 h-5 rounded border-2 transition-transform hover:scale-110',
                    color === borderColor && 'ring-2 ring-primary ring-offset-1'
                  )}
                  style={{ borderColor: color, backgroundColor: 'transparent' }}
                />
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function RichTextEditorChecklist({
  content,
  onChange,
  placeholder = 'Notizen hinzufügen...',
  className,
  minHeight = '200px',
  showProgress = true,
  showWordCount = true,
  editable = true,
  // Canvas props
  enableCanvas = false,
  drawingData = '',
  onDrawingChange,
}: RichTextEditorChecklistProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [storedFormat, setStoredFormat] = useState<any>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectionTick, setSelectionTick] = useState(0);
  const fullscreenRef = useRef<HTMLDivElement | null>(null);
  const isMountedRef = useRef(true);
  
  // Canvas state
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const editorContentRef = useRef<HTMLDivElement>(null);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [isTextMode, setIsTextMode] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [strokeColor, setStrokeColor] = useState("#000000");
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [isEraser, setIsEraser] = useState(false);
  const [canvasHistory, setCanvasHistory] = useState<string[]>([]);
  const [canvasHistoryIndex, setCanvasHistoryIndex] = useState(-1);
  const [isCanvasReady, setIsCanvasReady] = useState(false);
  
  // Text fields state
  const [textFields, setTextFields] = useState<CanvasTextField[]>([]);
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [draggingTextId, setDraggingTextId] = useState<string | null>(null);
  const textDragStartRef = useRef<{ x: number; y: number; fieldX: number; fieldY: number } | null>(null);
  
  // Resize state for text fields
  const [resizingTextId, setResizingTextId] = useState<string | null>(null);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const resizeStartRef = useRef<{ 
    x: number; y: number; 
    fieldX: number; fieldY: number; 
    fieldWidth: number; fieldHeight: number 
  } | null>(null);
  
  // Track if we've auto-shown canvas for current drawingData
  const autoShowTriggeredRef = useRef<string>('');

  const toggleFullscreen = async () => {
    const el = fullscreenRef.current;

    // Prefer native Fullscreen API (escapes dialog stacking and prevents click-through)
    if (el?.requestFullscreen) {
      try {
        if (document.fullscreenElement === el) {
          await document.exitFullscreen();
        } else {
          await el.requestFullscreen();
        }
        return;
      } catch {
        // fall through to CSS fullscreen
      }
    }

    // Fallback (no native fullscreen support)
    setIsFullscreen((v) => !v);
  };
  
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        // Disable default lists - we configure them separately for proper nesting
        bulletList: false,
        orderedList: false,
        listItem: false,
        strike: false, // We use our own Strike extension
      }),
      // List extensions configured separately for proper nested list support
      BulletList.configure({
        keepMarks: true,
        keepAttributes: false,
        HTMLAttributes: {
          class: 'list-disc list-outside ml-4',
        },
      }),
      OrderedList.configure({
        keepMarks: true,
        keepAttributes: false,
        HTMLAttributes: {
          class: 'list-decimal list-outside ml-4',
        },
      }),
      ListItem.configure({
        HTMLAttributes: {
          class: 'list-item',
        },
      }),
      Strike,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-primary underline cursor-pointer' },
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      FontFamily,
      FontSize,
      Color,
      Superscript,
      Subscript,
      Highlight.configure({
        multicolor: true,
      }),
      TiptapImage.configure({ inline: true, allowBase64: true }),
      Table.configure({ 
        resizable: true,
        HTMLAttributes: {
          class: 'editor-table',
        },
      }),
      TableRow,
      TableCell,
      TableHeader,
      TaskList.configure({ HTMLAttributes: { class: 'task-list' } }),
      CustomTaskItem.configure({ nested: true, HTMLAttributes: { class: 'task-item' } }),
    ],
    content: content || '',
    editable,
    editorProps: {
      attributes: {
        class: 'focus:outline-none text-sm h-full prose prose-sm max-w-none',
        style: 'padding: 12px;',
      },
      handleKeyDown: (view, event) => {
        // Ctrl+K for links
        if (event.ctrlKey && event.key === 'k') {
          event.preventDefault();
          setLinkDialogOpen(true);
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      if (isMountedRef.current) {
        onChange(editor.getHTML());
      }
    },
    immediatelyRender: false,
  });

  // Sync editable prop changes to Tiptap editor (important for collaboration mode toggle)
  useEffect(() => {
    if (editor && editor.isEditable !== editable) {
      editor.setEditable(editable);
    }
  }, [editor, editable]);

  // Store format from current selection
  const storeFormat = useCallback(() => {
    if (!editor) return;
    const marks = editor.state.selection.$from.marks();
    const attrs = editor.getAttributes('textStyle');
    setStoredFormat({ marks, attrs });
  }, [editor]);

  // Apply stored format
  const applyStoredFormat = useCallback(() => {
    if (!editor || !storedFormat) return;
    
    const { attrs } = storedFormat;
    if (attrs.fontSize) {
      editor.chain().focus().setMark('textStyle', { fontSize: attrs.fontSize }).run();
    }
    if (attrs.fontFamily) {
      editor.chain().focus().setFontFamily(attrs.fontFamily).run();
    }
    if (attrs.color) {
      editor.chain().focus().setColor(attrs.color).run();
    }
  }, [editor, storedFormat]);

  // Clear all formatting
  const clearFormatting = useCallback(() => {
    if (!editor) return;
    editor.chain().focus()
      .unsetAllMarks()
      .clearNodes()
      .run();
  }, [editor]);

  // Clipboard operations
  const handleCopy = useCallback(() => {
    document.execCommand('copy');
  }, []);

  const handleCut = useCallback(() => {
    document.execCommand('cut');
  }, []);

  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      editor?.chain().focus().insertContent(text).run();
    } catch {
      document.execCommand('paste');
    }
  }, [editor]);

  // Sort selected values (A→Z / Z→A)
  // - If multiple full blocks (paragraphs/headings) are selected: reorder those blocks
  // - If multiple list items are selected within the same list: reorder those list items
  // - Otherwise: sort selected text lines (split by \n) and replace the selection
  const sortSelection = useCallback(() => {
    if (!editor) return;

    const { state, view } = editor;
    const { from, to, empty, $from, $to } = state.selection;
    if (empty) return;

    const listTypes = new Set(['bulletList', 'orderedList', 'taskList']);

    const sortKey = (node: any) => (node?.textContent || '').trim();
    const cmp = (a: any, b: any) => sortKey(a).localeCompare(sortKey(b), 'de', { sensitivity: 'base' });

    const sharedDepth = $from.sharedDepth($to.pos);

    // 1) Try: sort list items within a shared list container
    for (let d = sharedDepth; d > 0; d--) {
      const container = $from.node(d);
      if (!listTypes.has(container.type.name)) continue;

      const containerStart = $from.start(d);
      const selected: Array<{ node: any; fromPos: number; toPos: number }> = [];
      let hasNonListItemInRange = false;

      container.forEach((child: any, offset: number) => {
        const childFrom = containerStart + offset;
        const childTo = childFrom + child.nodeSize;
        const overlaps = childFrom < to && childTo > from;

        if (overlaps && child.type.name !== 'listItem') {
          hasNonListItemInRange = true;
          return;
        }

        if (child.type.name !== 'listItem') return;

        // only include fully selected items
        const contentFrom = childFrom + 1;
        const contentTo = childTo - 1;
        const fullySelected = from <= contentFrom && to >= contentTo;

        if (fullySelected) {
          selected.push({ node: child, fromPos: childFrom, toPos: childTo });
        }
      });

      if (!hasNonListItemInRange && selected.length >= 2) {
        const rangeFrom = selected[0].fromPos;
        const rangeTo = selected[selected.length - 1].toPos;

        const sorted = [...selected].sort((a, b) => cmp(a.node, b.node));
        if (sortDirection === 'desc') sorted.reverse();

        const tr = state.tr.replaceWith(rangeFrom, rangeTo, Fragment.fromArray(sorted.map((s) => s.node)));
        view.dispatch(tr.scrollIntoView());
        editor.commands.focus();
        setSortDirection((dir) => (dir === 'asc' ? 'desc' : 'asc'));
        return;
      }

      // if we are inside a list but selection contains non-list-items or not enough fully-selected listItems,
      // we still allow fallback sorting of the raw selected text below.
      break;
    }

    // 2) Try: sort full text blocks (paragraphs/headings) within a shared container
    let containerDepth = sharedDepth;
    while (containerDepth > 0 && $from.node(containerDepth).isTextblock) containerDepth--;

    const container = $from.node(containerDepth);
    const containerStart = containerDepth === 0 ? 0 : $from.start(containerDepth);

    const selectedBlocks: Array<{ node: any; fromPos: number; toPos: number }> = [];
    let hasNonTextblockInRange = false;

    container.forEach((child: any, offset: number) => {
      const childFrom = containerStart + offset;
      const childTo = childFrom + child.nodeSize;
      const overlaps = childFrom < to && childTo > from;

      if (overlaps && !child.isTextblock) {
        hasNonTextblockInRange = true;
        return;
      }

      if (!child.isTextblock) return;

      // only include fully selected blocks
      const contentFrom = childFrom + 1;
      const contentTo = childTo - 1;
      const fullySelected = from <= contentFrom && to >= contentTo;

      if (fullySelected) {
        selectedBlocks.push({ node: child, fromPos: childFrom, toPos: childTo });
      }
    });

    if (!hasNonTextblockInRange && selectedBlocks.length >= 2) {
      const rangeFrom = selectedBlocks[0].fromPos;
      const rangeTo = selectedBlocks[selectedBlocks.length - 1].toPos;

      const sorted = [...selectedBlocks].sort((a, b) => cmp(a.node, b.node));
      if (sortDirection === 'desc') sorted.reverse();

      const tr = state.tr.replaceWith(rangeFrom, rangeTo, Fragment.fromArray(sorted.map((s) => s.node)));
      view.dispatch(tr.scrollIntoView());
      editor.commands.focus();
      setSortDirection((dir) => (dir === 'asc' ? 'desc' : 'asc'));
      return;
    }

    // 3) Fallback: sort selected text lines
    const rawText = state.doc.textBetween(from, to, '\n');
    const lines = rawText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

    if (lines.length < 2) return;

    const sortedLines = [...lines].sort((a, b) => a.localeCompare(b, 'de', { sensitivity: 'base' }));
    if (sortDirection === 'desc') sortedLines.reverse();

    editor.chain().focus().insertContentAt({ from, to }, sortedLines.join('\n')).run();
    setSortDirection((dir) => (dir === 'asc' ? 'desc' : 'asc'));
  }, [editor, sortDirection]);

  // ========== PHASE 2-5: NORMALIZED STROKE CANVAS LOGIC ==========
  
  // Authoritative strokes ref - source of truth for all drawings
  const strokesRef = useRef<CanvasStroke[]>([]);
  
  // Active stroke being drawn (points in normalized 0-1 coordinates)
  const activeStrokeRef = useRef<CanvasStroke | null>(null);
  
  // Authoritative drawing data ref - serialized JSON string
  const drawingDataRef = useRef<string>('');
  
  // Track last CSS point for incremental smooth drawing
  const lastCssPointRef = useRef<{ x: number; y: number } | null>(null);

  // Debounce timer for resize observer
  const resizeTimerRef = useRef<number | null>(null);

  // Track container visibility transitions (dialog closed -> open)
  const lastContainerSizeRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });

  // ========== PHASE 2: RENDERING ENGINE ==========
  
  // Render all strokes to canvas with proper scaling
  const renderStrokesToCanvas = useCallback((canvas: HTMLCanvasElement | null) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Reset transform for consistent rendering
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    
    // Get dynamic backing scale for consistent line thickness
    const rect = canvas.getBoundingClientRect();
    const backingScale = rect.width > 0 ? canvas.width / rect.width : 1;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Enable smooth rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Render all strokes
    for (const stroke of strokesRef.current) {
      if (stroke.points.length === 0) continue;
      
      ctx.globalCompositeOperation = stroke.isEraser ? 'destination-out' : 'source-over';
      ctx.strokeStyle = stroke.isEraser ? 'rgba(0,0,0,1)' : stroke.color;
      // Apply backing scale to maintain consistent visual thickness
      ctx.lineWidth = (stroke.isEraser ? stroke.width * 3 : stroke.width) * backingScale;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      ctx.beginPath();
      
      // Convert normalized coords to canvas coords
      const firstPt = stroke.points[0];
      ctx.moveTo(firstPt.x * canvas.width, firstPt.y * canvas.height);

      // Use quadratic curves for smooth lines
      for (let i = 1; i < stroke.points.length; i++) {
        const prev = stroke.points[i - 1];
        const curr = stroke.points[i];
        
        // Calculate midpoint for smooth curve
        const midX = ((prev.x + curr.x) / 2) * canvas.width;
        const midY = ((prev.y + curr.y) / 2) * canvas.height;
        
        if (i === 1) {
          ctx.lineTo(midX, midY);
        } else {
          const prevMidX = ((stroke.points[i - 2].x + prev.x) / 2) * canvas.width;
          const prevMidY = ((stroke.points[i - 2].y + prev.y) / 2) * canvas.height;
          ctx.quadraticCurveTo(prev.x * canvas.width, prev.y * canvas.height, midX, midY);
        }
      }
      
      // Draw to the last point
      if (stroke.points.length > 1) {
        const last = stroke.points[stroke.points.length - 1];
        ctx.lineTo(last.x * canvas.width, last.y * canvas.height);
      }
      
      ctx.stroke();
    }

    // Reset composite operation
    ctx.globalCompositeOperation = 'source-over';
  }, []);

  // Update ref when prop changes and re-render
  useEffect(() => {
    drawingDataRef.current = drawingData ?? '';
    
    // Parse and update strokes from external data
    const parsed = parseCanvasData(drawingData || '');
    strokesRef.current = parsed.strokes;
    
    // Re-render canvas with new strokes
    renderStrokesToCanvas(canvasRef.current);
  }, [drawingData, renderStrokesToCanvas]);
  
  // HiDPI canvas initialization - simplified for stroke-based rendering
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = editorContentRef.current;
    if (!canvas || !container || !enableCanvas) return;

    const dpr = window.devicePixelRatio || 1;
    const width = container.offsetWidth;
    const height = Math.max(container.offsetHeight, 300);
    
    if (width <= 0 || height <= 0) return;
    
    // Calculate target buffer size with DPR
    const targetWidth = Math.round(width * dpr);
    const targetHeight = Math.round(height * dpr);
    
    // Only resize if dimensions actually changed
    const needsResize = canvas.width !== targetWidth || canvas.height !== targetHeight;
    if (!needsResize) {
      setIsCanvasReady(true);
      return;
    }
    
    // Set CSS size to container size (display size)
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    
    // Set actual canvas buffer size with DPR scaling for HiDPI
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    
    // Mark canvas as ready for drawing
    setIsCanvasReady(true);
    
    // Re-render all strokes at new resolution
    renderStrokesToCanvas(canvas);
  }, [enableCanvas, renderStrokesToCanvas]);

  // Load and render drawing data when canvas becomes available
  const loadDrawingDataToCanvas = useCallback(() => {
    if (!enableCanvas) return;
    
    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0 || canvas.height === 0) return;
    
    // Parse the data to extract strokes
    const parsedData = parseCanvasData(drawingData || '');
    strokesRef.current = parsedData.strokes;
    
    // Render strokes to canvas
    renderStrokesToCanvas(canvas);
  }, [enableCanvas, drawingData, renderStrokesToCanvas]);

  // Load drawing data when it changes or canvas becomes available
  useEffect(() => {
    loadDrawingDataToCanvas();
  }, [loadDrawingDataToCanvas]);

  // ========== PHASE 3: RESIZE HANDLING ==========
  
  // Resize observer - re-renders strokes at new resolution (no quality loss)
  useEffect(() => {
    const container = editorContentRef.current;
    if (!enableCanvas || !container) return;

    const maybeAutoShowOnReopen = () => {
      const w = container.offsetWidth;
      const h = container.offsetHeight;
      const prev = lastContainerSizeRef.current;
      const wasHidden = prev.w === 0 || prev.h === 0;
      const isVisible = w > 0 && h > 0;
      lastContainerSizeRef.current = { w, h };

      if (wasHidden && isVisible) {
        const parsed = parseCanvasData(drawingDataRef.current || drawingData || '');
        const hasStrokes = parsed.strokes.length > 0;
        const hasTextFields = parsed.textFields.length > 0;

        // Only auto-enable on reopen transition
        if ((hasStrokes || hasTextFields) && !isDrawingMode) {
          setIsDrawingMode(true);
        }

        // Force a re-render
        requestAnimationFrame(() => {
          initCanvas();
        });
      }
    };

    const run = () => {
      maybeAutoShowOnReopen();
      initCanvas();
      
      lastContainerSizeRef.current = {
        w: container.offsetWidth,
        h: container.offsetHeight,
      };
    };

    // Initial setup
    const timeoutId = window.setTimeout(run, 80);

    const observer = new ResizeObserver(() => {
      if (resizeTimerRef.current) {
        window.clearTimeout(resizeTimerRef.current);
      }
      resizeTimerRef.current = window.setTimeout(run, 120);
    });

    observer.observe(container);
    return () => {
      window.clearTimeout(timeoutId);
      if (resizeTimerRef.current) window.clearTimeout(resizeTimerRef.current);
      observer.disconnect();
    };
  }, [enableCanvas, initCanvas, drawingData, isDrawingMode]);

  // ========== PHASE 3: STROKE-BASED HISTORY ==========
  
  const saveCanvasToHistory = useCallback(() => {
    // Save a copy of current strokes to history
    const strokesCopy = JSON.stringify(strokesRef.current);
    setCanvasHistory(prev => {
      const newHistory = prev.slice(0, canvasHistoryIndex + 1);
      newHistory.push(strokesCopy);
      return newHistory.slice(-20);
    });
    setCanvasHistoryIndex(prev => Math.min(prev + 1, 19));
  }, [canvasHistoryIndex]);

  // Get normalized coordinates (0-1) from pointer event
  const getNormalizedCoords = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    
    return {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
    };
  }, []);
  
  // Get CSS pixel coordinates from pointer event (for live drawing feedback)
  const getCssCoords = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  }, []);

  // Serialize and save current strokes + textFields
  const saveStrokesToDrawing = useCallback(() => {
    if (!onDrawingChange) return;
    
    const data: CanvasData = {
      strokes: strokesRef.current,
      textFields: textFields,
    };
    
    const serialized = serializeCanvasData(data);
    drawingDataRef.current = serialized;
    onDrawingChange(serialized);
  }, [onDrawingChange, textFields]);

  // Start drawing a new stroke
  const startCanvasDrawing = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingMode || !editable) return;
    event.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;
    
    // Ensure canvas is properly initialized
    if (!isCanvasReady || canvas.width === 0 || canvas.height === 0) {
      initCanvas();
      return;
    }
    
    // Capture pointer for reliable move/up events
    event.currentTarget.setPointerCapture(event.pointerId);
    
    setIsDrawing(true);

    const normalizedCoords = getNormalizedCoords(event);
    const cssCoords = getCssCoords(event);
    
    if (!normalizedCoords) return;
    
    // Start a new stroke
    activeStrokeRef.current = {
      points: [normalizedCoords],
      color: strokeColor,
      width: strokeWidth,
      isEraser: isEraser,
    };
    
    lastCssPointRef.current = cssCoords;
    
    // Draw immediate feedback
    const rect = canvas.getBoundingClientRect();
    const backingScale = rect.width > 0 ? canvas.width / rect.width : 1;
    
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
    ctx.strokeStyle = isEraser ? 'rgba(0,0,0,1)' : strokeColor;
    ctx.lineWidth = (isEraser ? strokeWidth * 3 : strokeWidth) * backingScale;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    // Draw initial point
    const canvasX = normalizedCoords.x * canvas.width;
    const canvasY = normalizedCoords.y * canvas.height;
    ctx.beginPath();
    ctx.moveTo(canvasX - 0.5, canvasY - 0.5);
    ctx.lineTo(canvasX + 0.5, canvasY + 0.5);
    ctx.stroke();
  }, [isDrawingMode, editable, isCanvasReady, initCanvas, getNormalizedCoords, getCssCoords, isEraser, strokeColor, strokeWidth]);

  // Continue drawing the active stroke
  const drawOnCanvas = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingMode || !editable || !isDrawing || !activeStrokeRef.current) return;
    event.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    const normalizedCoords = getNormalizedCoords(event);
    const cssCoords = getCssCoords(event);
    
    if (!normalizedCoords) return;
    
    const activeStroke = activeStrokeRef.current;
    const lastCss = lastCssPointRef.current;
    
    // Skip micro-movements
    if (lastCss) {
      const dx = cssCoords.x - lastCss.x;
      const dy = cssCoords.y - lastCss.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 1.5) return;
    }
    
    // Add point to active stroke
    activeStroke.points.push(normalizedCoords);
    
    // Draw incremental segment for immediate feedback
    const rect = canvas.getBoundingClientRect();
    const backingScale = rect.width > 0 ? canvas.width / rect.width : 1;
    
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
    ctx.strokeStyle = isEraser ? 'rgba(0,0,0,1)' : strokeColor;
    ctx.lineWidth = (isEraser ? strokeWidth * 3 : strokeWidth) * backingScale;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    // Re-render all completed strokes + active stroke for seamless appearance
    const points = activeStroke.points;
    if (points.length >= 1) {
      // First render all completed strokes
      renderStrokesToCanvas(canvas);
      
      // Then render the active stroke on top
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
      ctx.strokeStyle = isEraser ? 'rgba(0,0,0,1)' : strokeColor;
      ctx.lineWidth = (isEraser ? strokeWidth * 3 : strokeWidth) * backingScale;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      ctx.beginPath();
      const firstPt = points[0];
      ctx.moveTo(firstPt.x * canvas.width, firstPt.y * canvas.height);
      
      // Use quadratic curves for smooth lines (same as renderStrokesToCanvas)
      for (let i = 1; i < points.length; i++) {
        const prev = points[i - 1];
        const curr = points[i];
        
        const midX = ((prev.x + curr.x) / 2) * canvas.width;
        const midY = ((prev.y + curr.y) / 2) * canvas.height;
        
        if (i === 1) {
          ctx.lineTo(midX, midY);
        } else {
          const prevMidX = ((points[i - 2].x + prev.x) / 2) * canvas.width;
          const prevMidY = ((points[i - 2].y + prev.y) / 2) * canvas.height;
          ctx.quadraticCurveTo(prev.x * canvas.width, prev.y * canvas.height, midX, midY);
        }
      }
      
      // Draw to the last point
      if (points.length > 1) {
        const last = points[points.length - 1];
        ctx.lineTo(last.x * canvas.width, last.y * canvas.height);
      }
      
      ctx.stroke();
    }
    
    lastCssPointRef.current = cssCoords;
  }, [isDrawingMode, editable, isDrawing, getNormalizedCoords, getCssCoords, isEraser, strokeColor, strokeWidth]);

  // Finish the active stroke
  const stopCanvasDrawing = useCallback(() => {
    if (!isDrawing) return;
    setIsDrawing(false);
    
    // Finalize active stroke
    if (activeStrokeRef.current && activeStrokeRef.current.points.length > 0) {
      strokesRef.current = [...strokesRef.current, activeStrokeRef.current];
      activeStrokeRef.current = null;
      lastCssPointRef.current = null;
      
      // Save to history and persist
      saveCanvasToHistory();
      saveStrokesToDrawing();
      
      // Re-render for clean stroke appearance
      renderStrokesToCanvas(canvasRef.current);
    }
    
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      ctx.globalCompositeOperation = 'source-over';
    }
  }, [isDrawing, saveCanvasToHistory, saveStrokesToDrawing, renderStrokesToCanvas]);

  // Undo last stroke
  const undoCanvasDrawing = useCallback(() => {
    if (canvasHistoryIndex <= 0) return;
    
    const newIndex = canvasHistoryIndex - 1;
    
    try {
      // Restore strokes from history
      const strokesJson = canvasHistory[newIndex];
      strokesRef.current = JSON.parse(strokesJson);
      
      // Re-render
      renderStrokesToCanvas(canvasRef.current);
      
      // Save state
      saveStrokesToDrawing();
      setCanvasHistoryIndex(newIndex);
    } catch (err) {
      console.error('Failed to undo:', err);
    }
  }, [canvasHistoryIndex, canvasHistory, renderStrokesToCanvas, saveStrokesToDrawing]);

  // Clear all strokes
  const clearCanvasDrawing = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    // Clear strokes
    strokesRef.current = [];
    
    // Clear canvas
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Save to history and persist
    saveCanvasToHistory();
    saveStrokesToDrawing();
  }, [saveCanvasToHistory, saveStrokesToDrawing]);

  // ========== TEXT FIELDS LOGIC ==========
  
  // Track if we initiated the last save (to avoid re-parsing our own output)
  const lastSavedTextFieldsRef = useRef<string>('');
  
  // Load text fields from drawingData - only on initial mount or external changes
  useEffect(() => {
    if (!enableCanvas || !drawingData) return;
    
    // Skip if this is the result of our own save (prevents infinite loop)
    const parsed = parseCanvasData(drawingData);
    const serializedFields = JSON.stringify(parsed.textFields);
    if (serializedFields === lastSavedTextFieldsRef.current) {
      return; // Same data we just saved - skip
    }
    
    // Only update if actually different
    setTextFields(prev => {
      const prevSerialized = JSON.stringify(prev);
      if (prevSerialized === serializedFields) {
        return prev; // No change
      }
      return parsed.textFields;
    });
  }, [enableCanvas, drawingData]);
  
  // Add a new text field at click position
  const addTextField = useCallback((x: number, y: number) => {
    const newField: CanvasTextField = {
      id: `text-${Date.now()}`,
      x,
      y,
      text: 'Text',
      fontSize: 16,
      color: strokeColor,
      width: 150,
    };
    setTextFields(prev => [...prev, newField]);
    setEditingTextId(newField.id);
    setIsTextMode(false);
    
    // Save immediately
    saveTextFieldsToDrawing([...textFields, newField]);
  }, [strokeColor, textFields]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Save text fields along with strokes
  const saveTextFieldsToDrawing = useCallback((fields: CanvasTextField[]) => {
    if (!onDrawingChange) return;
    
    try {
      const data: CanvasData = { 
        strokes: strokesRef.current, 
        textFields: fields 
      };
      
      // Track what we're saving to avoid re-parsing our own output
      lastSavedTextFieldsRef.current = JSON.stringify(fields);
      
      const serialized = serializeCanvasData(data);
      drawingDataRef.current = serialized;
      onDrawingChange(serialized);
    } catch (err) {
      console.error('Failed to save text fields:', err);
    }
  }, [onDrawingChange]);
  
  // Update text field content
  const updateTextField = useCallback((id: string, updates: Partial<CanvasTextField>) => {
    setTextFields(prev => {
      const updated = prev.map(f => f.id === id ? { ...f, ...updates } : f);
      saveTextFieldsToDrawing(updated);
      return updated;
    });
  }, [saveTextFieldsToDrawing]);
  
  // Delete text field
  const deleteTextField = useCallback((id: string) => {
    setTextFields(prev => {
      const updated = prev.filter(f => f.id !== id);
      saveTextFieldsToDrawing(updated);
      return updated;
    });
    if (editingTextId === id) setEditingTextId(null);
  }, [editingTextId, saveTextFieldsToDrawing]);
  
  // Start dragging text field
  const startTextDrag = useCallback((e: React.PointerEvent, field: CanvasTextField) => {
    if (editingTextId === field.id) return; // Don't drag while editing
    e.preventDefault();
    e.stopPropagation();
    setDraggingTextId(field.id);
    textDragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      fieldX: field.x,
      fieldY: field.y,
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [editingTextId]);
  
  // Handle text drag move
  const handleTextDrag = useCallback((e: React.PointerEvent) => {
    if (!draggingTextId || !textDragStartRef.current) return;
    
    const container = editorContentRef.current;
    if (!container) return;
    
    const rect = container.getBoundingClientRect();
    const deltaX = e.clientX - textDragStartRef.current.x;
    const deltaY = e.clientY - textDragStartRef.current.y;
    
    const newX = Math.max(0, Math.min(rect.width - 50, textDragStartRef.current.fieldX + deltaX));
    const newY = Math.max(0, Math.min(rect.height - 20, textDragStartRef.current.fieldY + deltaY));
    
    setTextFields(prev => prev.map(f => 
      f.id === draggingTextId ? { ...f, x: newX, y: newY } : f
    ));
  }, [draggingTextId]);
  
  // Stop text drag
  const stopTextDrag = useCallback(() => {
    if (draggingTextId) {
      saveTextFieldsToDrawing(textFields);
    }
    setDraggingTextId(null);
    textDragStartRef.current = null;
  }, [draggingTextId, textFields, saveTextFieldsToDrawing]);
  
  // Start resizing text field
  const startTextResize = useCallback((e: React.PointerEvent, field: CanvasTextField, handle: string) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingTextId(field.id);
    setResizeHandle(handle);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      fieldX: field.x,
      fieldY: field.y,
      fieldWidth: field.width,
      fieldHeight: field.height || 40,
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);
  
  // Handle resize move
  const handleTextResize = useCallback((e: React.PointerEvent) => {
    if (!resizingTextId || !resizeStartRef.current || !resizeHandle) return;
    
    const deltaX = e.clientX - resizeStartRef.current.x;
    const deltaY = e.clientY - resizeStartRef.current.y;
    
    setTextFields(prev => prev.map(f => {
      if (f.id !== resizingTextId) return f;
      
      let newX = f.x;
      let newY = f.y;
      let newWidth = resizeStartRef.current!.fieldWidth;
      let newHeight = resizeStartRef.current!.fieldHeight;
      
      // Handle horizontal resize
      if (resizeHandle.includes('e')) {
        newWidth = Math.max(50, resizeStartRef.current!.fieldWidth + deltaX);
      }
      if (resizeHandle.includes('w')) {
        const widthDelta = -deltaX;
        newWidth = Math.max(50, resizeStartRef.current!.fieldWidth + widthDelta);
        newX = resizeStartRef.current!.fieldX - widthDelta;
      }
      
      // Handle vertical resize
      if (resizeHandle.includes('s')) {
        newHeight = Math.max(24, resizeStartRef.current!.fieldHeight + deltaY);
      }
      if (resizeHandle.includes('n')) {
        const heightDelta = -deltaY;
        newHeight = Math.max(24, resizeStartRef.current!.fieldHeight + heightDelta);
        newY = resizeStartRef.current!.fieldY - heightDelta;
      }
      
      return { ...f, x: newX, y: newY, width: newWidth, height: newHeight };
    }));
  }, [resizingTextId, resizeHandle]);
  
  // Stop resize
  const stopTextResize = useCallback(() => {
    if (resizingTextId) {
      saveTextFieldsToDrawing(textFields);
    }
    setResizingTextId(null);
    setResizeHandle(null);
    resizeStartRef.current = null;
  }, [resizingTextId, textFields, saveTextFieldsToDrawing]);
  
  // Handle canvas click for text mode
  const handleCanvasClickForText = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isTextMode || !editable) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    addTextField(x, y);
  }, [isTextMode, editable, addTextField]);

  // Exit drawing mode when disabling canvas
  useEffect(() => {
    if (!enableCanvas) {
      setIsDrawingMode(false);
      setIsTextMode(false);
    }
  }, [enableCanvas]);
  
  // Auto-show drawing mode when there's existing drawing content
  useEffect(() => {
    if (!enableCanvas) return;
    
    // Check if there's actual drawing content
    const parsed = parseCanvasData(drawingData || '');
    const hasStrokes = parsed.strokes.length > 0;
    const hasTextFields = parsed.textFields.length > 0;
    
    // Create a signature to track if we've already auto-shown for this data
    const dataSignature = `${hasStrokes ? 'S' : ''}${hasTextFields ? 'T' : ''}${drawingData?.slice(0, 50) || ''}`;
    
    if ((hasStrokes || hasTextFields) && autoShowTriggeredRef.current !== dataSignature) {
      autoShowTriggeredRef.current = dataSignature;
      // Auto-enable drawing mode to show the canvas immediately
      setIsDrawingMode(true);
    }
  }, [enableCanvas, drawingData]); // React to both enableCanvas AND drawingData changes

  // ========== END CANVAS LOGIC ==========

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      if (editor && !editor.isDestroyed) {
        editor.destroy();
      }
    };
  }, [editor]);

  // Sync content from outside
  useEffect(() => {
    if (editor && !editor.isDestroyed && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  // Re-render on cursor/selection changes (needed for enabling toolbar buttons like Sort)
  useEffect(() => {
    if (!editor) return;

    let raf: number | null = null;
    const handler = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setSelectionTick((t) => t + 1);
        raf = null;
      });
    };

    editor.on('selectionUpdate', handler);
    return () => {
      editor.off('selectionUpdate', handler);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [editor]);

  // Keep isFullscreen in sync with the native Fullscreen API
  useEffect(() => {
    const onChange = () => {
      const el = fullscreenRef.current;
      setIsFullscreen(!!el && document.fullscreenElement === el);
    };

    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // ESC should ONLY exit fullscreen (and must not close the underlying dialog)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !isFullscreen) return;

      e.preventDefault();
      e.stopPropagation();
      // stopImmediatePropagation exists on native Event
      (e as unknown as { stopImmediatePropagation?: () => void }).stopImmediatePropagation?.();

      if (document.fullscreenElement) {
        void document.exitFullscreen();
      } else {
        setIsFullscreen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true } as any);
  }, [isFullscreen]);

  // Calculate stats
  const stats = useMemo(() => {
    if (!content) return { words: 0, chars: 0, completed: 0, total: 0, percentage: 0 };
    
    const textContent = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const words = textContent ? textContent.split(/\s+/).length : 0;
    const chars = textContent.length;
    
    const taskItemRegex = /<li[^>]*data-checked="(true|false)"[^>]*>/g;
    let total = 0;
    let completed = 0;
    let match;
    while ((match = taskItemRegex.exec(content)) !== null) {
      total++;
      if (match[1] === 'true') completed++;
    }
    
    return {
      words,
      chars,
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }, [content]);

  if (!editor || editor.isDestroyed) return null;

  const currentFontSize = editor.getAttributes('textStyle').fontSize || null;
  const currentTextColor = editor.getAttributes('textStyle').color || null;
  const currentHighlight = editor.getAttributes('highlight').color || null;

  void selectionTick; // forces toolbar to refresh on selection changes
  const canSort = (() => {
    const { from, to, empty } = editor.state.selection;
    if (empty) return false;

    const rawText = editor.state.doc.textBetween(from, to, '\n');
    const lines = rawText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

    return lines.length >= 2;
  })();

  const sortTitle = sortDirection === 'asc' ? 'Sortieren A→Z' : 'Sortieren Z→A';

  const editorContent = (
    <div 
      ref={fullscreenRef}
      className={cn(
        'border rounded-lg bg-background flex flex-col h-full',
        isFullscreen && 'fixed inset-0 z-[9999] rounded-none',
        !isFullscreen && className
      )} 
      style={!isFullscreen ? { minHeight } : undefined}
    >
      {/* Toolbar - grouped dropdowns for compactness */}
      {editable && (
        <div className={cn(
          "border-b bg-muted/30 p-1.5",
          !isFullscreen && "rounded-t-lg"
        )}>
          <div className="flex items-center flex-wrap gap-0.5">
            {/* Clipboard Dropdown */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 px-2 gap-0.5" title="Zwischenablage">
                  <ClipboardPaste className="h-4 w-4" />
                  <span className="text-[10px] text-muted-foreground">▼</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-40 p-1 z-[10000]" align="start" portalled={isFullscreen ? false : true}>
                <button onClick={handleCopy} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded">
                  <Copy className="h-4 w-4" /> Kopieren
                </button>
                <button onClick={handleCut} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded">
                  <Scissors className="h-4 w-4" /> Ausschneiden
                </button>
                <button onClick={handlePaste} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded">
                  <ClipboardPaste className="h-4 w-4" /> Einfügen
                </button>
              </PopoverContent>
            </Popover>

            <Divider />

            {/* Text formatting - frequent actions stay visible */}
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleBold().run()}
              isActive={editor.isActive('bold')}
              title="Fett (Strg+B)"
            >
              <Bold className="h-4 w-4" />
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleItalic().run()}
              isActive={editor.isActive('italic')}
              title="Kursiv (Strg+I)"
            >
              <Italic className="h-4 w-4" />
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              isActive={editor.isActive('underline')}
              title="Unterstrichen (Strg+U)"
            >
              <UnderlineIcon className="h-4 w-4" />
            </ToolbarBtn>

            {/* More Format Dropdown */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 px-2 gap-0.5" title="Weitere Formatierung">
                  <Strikethrough className="h-4 w-4" />
                  <span className="text-[10px] text-muted-foreground">▼</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-44 p-1 z-[10000]" align="start" portalled={isFullscreen ? false : true}>
                <button 
                  onClick={() => editor.chain().focus().toggleStrike().run()}
                  className={cn("w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded", editor.isActive('strike') && "bg-accent")}
                >
                  <Strikethrough className="h-4 w-4" /> Durchgestrichen
                </button>
                <button 
                  onClick={() => editor.chain().focus().toggleSuperscript().run()}
                  className={cn("w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded", editor.isActive('superscript') && "bg-accent")}
                >
                  <SuperscriptIcon className="h-4 w-4" /> Hochgestellt
                </button>
                <button 
                  onClick={() => editor.chain().focus().toggleSubscript().run()}
                  className={cn("w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded", editor.isActive('subscript') && "bg-accent")}
                >
                  <SubscriptIcon className="h-4 w-4" /> Tiefgestellt
                </button>
                <div className="h-px bg-border my-1" />
                <button 
                  onClick={clearFormatting}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded"
                >
                  <RemoveFormatting className="h-4 w-4" /> Format löschen
                </button>
              </PopoverContent>
            </Popover>

            <Divider />
            
            {/* Headings Dropdown */}
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className={cn(
                    "h-8 px-2 gap-0.5",
                    (editor.isActive('heading', { level: 1 }) || editor.isActive('heading', { level: 2 }) || editor.isActive('heading', { level: 3 })) && "bg-accent"
                  )} 
                  title="Überschriften"
                >
                  <Heading1 className="h-4 w-4" />
                  <span className="text-[10px] text-muted-foreground">▼</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-44 p-1 z-[10000]" align="start" portalled={isFullscreen ? false : true}>
                <button 
                  onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                  className={cn("w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded", editor.isActive('heading', { level: 1 }) && "bg-accent")}
                >
                  <Heading1 className="h-4 w-4" /> <span className="text-lg font-bold">Überschrift 1</span>
                </button>
                <button 
                  onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  className={cn("w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded", editor.isActive('heading', { level: 2 }) && "bg-accent")}
                >
                  <Heading2 className="h-4 w-4" /> <span className="text-base font-bold">Überschrift 2</span>
                </button>
                <button 
                  onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                  className={cn("w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded", editor.isActive('heading', { level: 3 }) && "bg-accent")}
                >
                  <Heading3 className="h-4 w-4" /> <span className="text-sm font-bold">Überschrift 3</span>
                </button>
              </PopoverContent>
            </Popover>

            <Divider />
            
            {/* Font family & size - keep visible */}
            <FontFamilyPicker
              currentFamily={editor.getAttributes('textStyle').fontFamily || null}
              onSelect={(family) => {
                if (family) {
                  editor.chain().focus().setFontFamily(family).run();
                } else {
                  editor.chain().focus().unsetFontFamily().run();
                }
              }}
            />
            <FontSizePicker
              currentSize={currentFontSize}
              onSelect={(size) => {
                if (size) {
                  editor.chain().focus().setMark('textStyle', { fontSize: size }).run();
                } else {
                  editor.chain().focus().unsetMark('textStyle').run();
                }
              }}
              onIncrease={() => {
                const current = parseInt(currentFontSize || '14');
                const idx = FONT_SIZES.findIndex(s => parseInt(s) >= current);
                const nextIdx = Math.min(idx + 1, FONT_SIZES.length - 1);
                editor.chain().focus().setMark('textStyle', { fontSize: FONT_SIZES[nextIdx] }).run();
              }}
              onDecrease={() => {
                const current = parseInt(currentFontSize || '14');
                const idx = FONT_SIZES.findIndex(s => parseInt(s) >= current);
                const prevIdx = Math.max(idx - 1, 0);
                editor.chain().focus().setMark('textStyle', { fontSize: FONT_SIZES[prevIdx] }).run();
              }}
            />

            <Divider />
            
            {/* Lists & Indentation Dropdown */}
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className={cn(
                    "h-8 px-2 gap-0.5",
                    (editor.isActive('bulletList') || editor.isActive('orderedList') || editor.isActive('taskList')) && "bg-accent"
                  )} 
                  title="Listen & Einzug"
                >
                  <List className="h-4 w-4" />
                  <span className="text-[10px] text-muted-foreground">▼</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-44 p-1 z-[10000]" align="start" portalled={isFullscreen ? false : true}>
                <button 
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  className={cn("w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded", editor.isActive('bulletList') && "bg-accent")}
                >
                  <List className="h-4 w-4" /> Aufzählung
                </button>
                <button 
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  className={cn("w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded", editor.isActive('orderedList') && "bg-accent")}
                >
                  <ListOrdered className="h-4 w-4" /> Nummeriert
                </button>
                <button 
                  onClick={() => editor.chain().focus().toggleTaskList().run()}
                  className={cn("w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded", editor.isActive('taskList') && "bg-accent")}
                >
                  <CheckSquare className="h-4 w-4" /> Checkliste
                </button>
                <div className="h-px bg-border my-1" />
                <button 
                  onClick={() => {
                    if (editor.can().sinkListItem('listItem')) {
                      editor.chain().focus().sinkListItem('listItem').run();
                    } else if (editor.can().sinkListItem('taskItem')) {
                      editor.chain().focus().sinkListItem('taskItem').run();
                    }
                  }}
                  disabled={!editor.can().sinkListItem('listItem') && !editor.can().sinkListItem('taskItem')}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded disabled:opacity-50"
                >
                  <IndentIncrease className="h-4 w-4" /> Einzug +
                </button>
                <button 
                  onClick={() => {
                    if (editor.can().liftListItem('listItem')) {
                      editor.chain().focus().liftListItem('listItem').run();
                    } else if (editor.can().liftListItem('taskItem')) {
                      editor.chain().focus().liftListItem('taskItem').run();
                    }
                  }}
                  disabled={!editor.can().liftListItem('listItem') && !editor.can().liftListItem('taskItem')}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded disabled:opacity-50"
                >
                  <IndentDecrease className="h-4 w-4" /> Einzug -
                </button>
              </PopoverContent>
            </Popover>

            {/* Alignment Dropdown */}
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 px-2 gap-0.5"
                  title="Ausrichtung"
                >
                  <AlignLeft className="h-4 w-4" />
                  <span className="text-[10px] text-muted-foreground">▼</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-40 p-1 z-[10000]" align="start" portalled={isFullscreen ? false : true}>
                <button 
                  onClick={() => editor.chain().focus().setTextAlign('left').run()}
                  className={cn("w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded", editor.isActive({ textAlign: 'left' }) && "bg-accent")}
                >
                  <AlignLeft className="h-4 w-4" /> Links
                </button>
                <button 
                  onClick={() => editor.chain().focus().setTextAlign('center').run()}
                  className={cn("w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded", editor.isActive({ textAlign: 'center' }) && "bg-accent")}
                >
                  <AlignCenter className="h-4 w-4" /> Mitte
                </button>
                <button 
                  onClick={() => editor.chain().focus().setTextAlign('right').run()}
                  className={cn("w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded", editor.isActive({ textAlign: 'right' }) && "bg-accent")}
                >
                  <AlignRight className="h-4 w-4" /> Rechts
                </button>
                <button 
                  onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                  className={cn("w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded", editor.isActive({ textAlign: 'justify' }) && "bg-accent")}
                >
                  <AlignJustify className="h-4 w-4" /> Blocksatz
                </button>
              </PopoverContent>
            </Popover>

            <Divider />

            {/* Colors - keep visible */}
            <FullColorPicker
              currentColor={currentTextColor}
              onSelect={(color) => {
                if (color) {
                  editor.chain().focus().setColor(color).run();
                } else {
                  editor.chain().focus().unsetColor().run();
                }
              }}
              icon={Palette}
              tooltip="Textfarbe"
              type="text"
            />
            <FullColorPicker
              currentColor={currentHighlight}
              onSelect={(color) => {
                if (color) {
                  editor.chain().focus().toggleHighlight({ color }).run();
                } else {
                  editor.chain().focus().unsetHighlight().run();
                }
              }}
              icon={Highlighter}
              tooltip="Markierung"
              type="highlight"
            />

            <Divider />

            {/* Link Dialog - separate for Insert dropdown */}
            <LinkDialog
              onSubmit={(url) => editor.chain().focus().setLink({ href: url }).run()}
              onRemove={() => editor.chain().focus().unsetLink().run()}
              initialUrl={editor.getAttributes('link').href || ''}
              hasLink={editor.isActive('link')}
              isOpen={linkDialogOpen}
              onOpenChange={setLinkDialogOpen}
            />

            {/* Insert Dropdown */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 px-2 gap-0.5" title="Einfügen">
                  <PlusSquare className="h-4 w-4" />
                  <span className="text-[10px] text-muted-foreground">▼</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-44 p-1 z-[10000]" align="start" portalled={isFullscreen ? false : true}>
                <div className="space-y-0.5">
                  <button 
                    onClick={() => editor.chain().focus().setHorizontalRule().run()}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded"
                  >
                    <Minus className="h-4 w-4" /> Trennlinie
                  </button>
                  <button 
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    className={cn("w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded", editor.isActive('blockquote') && "bg-accent")}
                  >
                    <Quote className="h-4 w-4" /> Zitat
                  </button>
                  <button 
                    onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                    className={cn("w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded", editor.isActive('codeBlock') && "bg-accent")}
                  >
                    <Code className="h-4 w-4" /> Code-Block
                  </button>
                </div>
              </PopoverContent>
            </Popover>

            {/* Image Dialog - separate */}
            <ImageDialog onSubmit={(url) => editor.chain().focus().setImage({ src: url }).run()} />

            {/* Table Menu - separate */}
            <TableMenu
              onInsert={(rows, cols) => editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run()}
              onAddRow={() => editor.chain().focus().addRowAfter().run()}
              onAddColumn={() => editor.chain().focus().addColumnAfter().run()}
              onDeleteRow={() => editor.chain().focus().deleteRow().run()}
              onDeleteColumn={() => editor.chain().focus().deleteColumn().run()}
              onDeleteTable={() => editor.chain().focus().deleteTable().run()}
              hasTable={editor.isActive('table')}
            />

            {/* Extras Dropdown */}
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className={cn("h-8 px-2 gap-0.5", storedFormat && "bg-accent")} 
                  title="Extras"
                >
                  <Paintbrush className="h-4 w-4" />
                  <span className="text-[10px] text-muted-foreground">▼</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-1 z-[10000]" align="start" portalled={isFullscreen ? false : true}>
                <button 
                  onClick={storeFormat}
                  className={cn("w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded", storedFormat && "bg-accent")}
                >
                  <Paintbrush className="h-4 w-4" /> Format übernehmen
                </button>
                {storedFormat && (
                  <button 
                    onClick={applyStoredFormat}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded"
                  >
                    <Paintbrush className="h-4 w-4 text-primary" /> Format anwenden
                  </button>
                )}
                <div className="h-px bg-border my-1" />
                <button 
                  onClick={sortSelection}
                  disabled={!canSort}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded disabled:opacity-50"
                  title={sortTitle}
                >
                  <ArrowUpDown className="h-4 w-4" /> Sortieren
                </button>
              </PopoverContent>
            </Popover>

            {/* Search & Replace - separate */}
            <SearchReplaceDialog editor={editor} />

            {/* Fill & Border - separate */}
            <FillBorderPicker editor={editor} />

            <Divider />

            {/* Undo/Redo - always visible */}
            <ToolbarBtn
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
              title="Rückgängig (Strg+Z)"
            >
              <Undo className="h-4 w-4" />
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
              title="Wiederholen (Strg+Y)"
            >
              <Redo className="h-4 w-4" />
            </ToolbarBtn>

            {/* Canvas Drawing Tools - only when enableCanvas is true */}
            {enableCanvas && (
              <>
                <Divider />
                
                {/* Drawing mode toggle */}
                <ToolbarBtn
                  onClick={() => {
                    setIsDrawingMode(!isDrawingMode);
                    if (!isDrawingMode) setIsTextMode(false);
                  }}
                  isActive={isDrawingMode}
                  title={isDrawingMode ? "Zeichenmodus beenden" : "Zeichnen aktivieren"}
                >
                  <PencilLine className="h-4 w-4" />
                </ToolbarBtn>

                {/* Text mode toggle */}
                <ToolbarBtn
                  onClick={() => {
                    setIsTextMode(!isTextMode);
                    if (!isTextMode) setIsDrawingMode(false);
                  }}
                  isActive={isTextMode}
                  title={isTextMode ? "Textmodus beenden" : "Text hinzufügen"}
                >
                  <Type className="h-4 w-4" />
                </ToolbarBtn>

                {/* Drawing tools - only visible in drawing mode */}
                {isDrawingMode && (
                  <>
                    {/* Color picker */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          title="Stiftfarbe"
                        >
                          <div 
                            className="w-4 h-4 rounded-full border"
                            style={{ backgroundColor: strokeColor }}
                          />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-2 z-[10000]" align="start" portalled={isFullscreen ? false : true}>
                        <div className="grid grid-cols-4 gap-1">
                          {CANVAS_COLORS.map((color) => (
                            <button
                              key={color}
                              className={cn(
                                "w-8 h-8 rounded-full border-2 transition-transform hover:scale-110",
                                strokeColor === color ? "border-primary ring-2 ring-primary/30" : "border-transparent"
                              )}
                              style={{ backgroundColor: color }}
                              onClick={() => {
                                setStrokeColor(color);
                                setIsEraser(false);
                              }}
                            />
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>

                    {/* Stroke width */}
                    <div className="flex items-center gap-1 px-1">
                      <Circle className="h-3 w-3 text-muted-foreground" />
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={strokeWidth}
                        onChange={(e) => setStrokeWidth(Number(e.target.value))}
                        className="w-16 h-2"
                        title={`Strichstärke: ${strokeWidth}px`}
                      />
                      <Circle className="h-4 w-4 text-muted-foreground" />
                    </div>

                    {/* Eraser */}
                    <ToolbarBtn
                      onClick={() => setIsEraser(!isEraser)}
                      isActive={isEraser}
                      title="Radierer"
                    >
                      <Eraser className="h-4 w-4" />
                    </ToolbarBtn>

                    {/* Undo drawing */}
                    <ToolbarBtn
                      onClick={undoCanvasDrawing}
                      disabled={canvasHistoryIndex <= 0}
                      title="Zeichnung rückgängig"
                    >
                      <Undo2 className="h-4 w-4" />
                    </ToolbarBtn>

                    {/* Clear drawing */}
                    <ToolbarBtn
                      onClick={clearCanvasDrawing}
                      title="Zeichnung löschen"
                    >
                      <Trash2 className="h-4 w-4" />
                    </ToolbarBtn>
                  </>
                )}
              </>
            )}

            {/* Spacer pushes fullscreen button to the right */}
            <div className="flex-1" />

            {/* Fullscreen toggle */}
            <ToolbarBtn
              onClick={toggleFullscreen}
              title={isFullscreen ? "Vollbild beenden (Esc)" : "Vollbild"}
            >
              {isFullscreen ? <X className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </ToolbarBtn>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      {showProgress && stats.total > 0 && (
        <div className="px-3 py-2 border-b bg-muted/20">
          <div className="flex items-center gap-3">
            <Progress value={stats.percentage} className="h-2 flex-1" />
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {stats.completed}/{stats.total} ({stats.percentage}%)
            </span>
          </div>
        </div>
      )}

      {/* Editor Content with Canvas Overlay */}
      <div 
        ref={editorContentRef}
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain relative w-full cursor-text"
        style={!isFullscreen ? { maxHeight: minHeight } : undefined}
        onClick={(e) => {
          // Focus editor when clicking on empty area (not when in drawing mode)
          if (e.target === e.currentTarget && editor && !isDrawingMode) {
            editor.commands.focus('end');
          }
        }}
      >
        <EditorContent 
          editor={editor} 
          className={cn(
            'h-full [&_.ProseMirror]:min-h-full [&_.ProseMirror]:p-3',
            !editable && 'cursor-default',
            (isDrawingMode || isTextMode) && 'pointer-events-none'
          )}
        />
        
        {/* Canvas Overlay - always rendered when enableCanvas is true for preview; pointer-events only when drawing/text mode */}
        {enableCanvas && (
          <canvas
            ref={canvasRef}
            className={cn(
              "absolute inset-0 z-10 w-full h-full",
              isDrawingMode 
                ? "cursor-crosshair" 
                : isTextMode
                  ? "cursor-text"
                  : "pointer-events-none cursor-default"
            )}
            style={{ backgroundColor: 'transparent', touchAction: 'none' }}
            onPointerDown={startCanvasDrawing}
            onPointerMove={drawOnCanvas}
            onPointerUp={stopCanvasDrawing}
            onPointerCancel={stopCanvasDrawing}
            onPointerLeave={stopCanvasDrawing}
            onClick={handleCanvasClickForText}
          />
        )}
        
        {/* Text Fields Overlay - rendered as HTML for editing */}
        {enableCanvas && textFields.map((field) => {
          const isEditing = editingTextId === field.id;
          const isDragging = draggingTextId === field.id;
          const isResizing = resizingTextId === field.id;
          const fieldHeight = field.height || 40;
          
          return (
            <div
              key={field.id}
              className={cn(
                "absolute z-20 select-none group",
                !isEditing && !isTextMode && "cursor-move",
                isEditing && "ring-2 ring-primary cursor-text pointer-events-auto",
                (isDragging || isResizing) && "opacity-75"
              )}
              style={{
                left: field.x,
                top: field.y,
                width: field.width,
                height: fieldHeight,
              }}
              // Only attach drag handlers when NOT editing AND NOT in text mode
              onPointerDown={isEditing || isTextMode ? undefined : (e) => startTextDrag(e, field)}
              onPointerMove={isEditing || isTextMode ? undefined : (isDragging ? handleTextDrag : isResizing ? handleTextResize : undefined)}
              onPointerUp={isEditing || isTextMode ? undefined : () => { stopTextDrag(); stopTextResize(); }}
              onPointerCancel={isEditing || isTextMode ? undefined : () => { stopTextDrag(); stopTextResize(); }}
              onDoubleClick={isTextMode ? undefined : () => setEditingTextId(field.id)}
            >
              {isEditing ? (
                <div 
                  data-text-field-wrapper
                  className="relative pointer-events-auto w-full h-full"
                  // Stop ALL pointer events from bubbling during editing
                  onPointerDown={(e) => e.stopPropagation()}
                  onPointerMove={(e) => e.stopPropagation()}
                  onPointerUp={(e) => e.stopPropagation()}
                  onPointerCancel={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseMove={(e) => e.stopPropagation()}
                  onMouseUp={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <textarea
                    autoFocus
                    value={field.text}
                    onChange={(e) => updateTextField(field.id, { text: e.target.value })}
                    onBlur={(e) => {
                      // Don't close editing if focus moves to a related element (e.g., delete button)
                      const relatedTarget = e.relatedTarget as HTMLElement | null;
                      const parent = e.currentTarget.closest('[data-text-field-wrapper]');
                      if (relatedTarget && parent?.contains(relatedTarget)) {
                        // Focus is moving within the text field container, don't close
                        return;
                      }
                      // Use a small timeout to allow click events to fire first
                      setTimeout(() => {
                        setEditingTextId((currentId) => currentId === field.id ? null : currentId);
                      }, 100);
                    }}
                    onKeyDown={(e) => {
                      // Prevent event bubbling for all keys while editing
                      e.stopPropagation();
                      if (e.key === 'Escape') setEditingTextId(null);
                      if (e.key === 'Delete' && e.shiftKey) {
                        e.preventDefault();
                        deleteTextField(field.id);
                      }
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    onPointerMove={(e) => e.stopPropagation()}
                    onPointerUp={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="w-full h-full bg-background/90 border border-primary rounded px-1 py-0.5 resize-none focus:outline-none pointer-events-auto"
                    style={{
                      fontSize: field.fontSize,
                      color: field.color,
                    }}
                  />
                  <button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteTextField(field.id);
                    }}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center text-xs hover:bg-destructive/80"
                    title="Löschen (Shift+Entf)"
                  >
                    ×
                  </button>
                  
                  {/* Resize handles for editing mode */}
                  {/* Edge handles */}
                  <div 
                    className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full cursor-n-resize opacity-0 group-hover:opacity-100 hover:!opacity-100"
                    onPointerDown={(e) => { e.stopPropagation(); startTextResize(e, field, 'n'); }}
                    onPointerMove={(e) => { e.stopPropagation(); handleTextResize(e); }}
                    onPointerUp={(e) => { e.stopPropagation(); stopTextResize(); }}
                  />
                  <div 
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-3 h-3 bg-primary rounded-full cursor-s-resize opacity-0 group-hover:opacity-100 hover:!opacity-100"
                    onPointerDown={(e) => { e.stopPropagation(); startTextResize(e, field, 's'); }}
                    onPointerMove={(e) => { e.stopPropagation(); handleTextResize(e); }}
                    onPointerUp={(e) => { e.stopPropagation(); stopTextResize(); }}
                  />
                  <div 
                    className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full cursor-w-resize opacity-0 group-hover:opacity-100 hover:!opacity-100"
                    onPointerDown={(e) => { e.stopPropagation(); startTextResize(e, field, 'w'); }}
                    onPointerMove={(e) => { e.stopPropagation(); handleTextResize(e); }}
                    onPointerUp={(e) => { e.stopPropagation(); stopTextResize(); }}
                  />
                  <div 
                    className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full cursor-e-resize opacity-0 group-hover:opacity-100 hover:!opacity-100"
                    onPointerDown={(e) => { e.stopPropagation(); startTextResize(e, field, 'e'); }}
                    onPointerMove={(e) => { e.stopPropagation(); handleTextResize(e); }}
                    onPointerUp={(e) => { e.stopPropagation(); stopTextResize(); }}
                  />
                  {/* Corner handles */}
                  <div 
                    className="absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full cursor-nw-resize opacity-0 group-hover:opacity-100 hover:!opacity-100"
                    onPointerDown={(e) => { e.stopPropagation(); startTextResize(e, field, 'nw'); }}
                    onPointerMove={(e) => { e.stopPropagation(); handleTextResize(e); }}
                    onPointerUp={(e) => { e.stopPropagation(); stopTextResize(); }}
                  />
                  <div 
                    className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full cursor-ne-resize opacity-0 group-hover:opacity-100 hover:!opacity-100"
                    onPointerDown={(e) => { e.stopPropagation(); startTextResize(e, field, 'ne'); }}
                    onPointerMove={(e) => { e.stopPropagation(); handleTextResize(e); }}
                    onPointerUp={(e) => { e.stopPropagation(); stopTextResize(); }}
                  />
                  <div 
                    className="absolute bottom-0 left-0 -translate-x-1/2 translate-y-1/2 w-3 h-3 bg-primary rounded-full cursor-sw-resize opacity-0 group-hover:opacity-100 hover:!opacity-100"
                    onPointerDown={(e) => { e.stopPropagation(); startTextResize(e, field, 'sw'); }}
                    onPointerMove={(e) => { e.stopPropagation(); handleTextResize(e); }}
                    onPointerUp={(e) => { e.stopPropagation(); stopTextResize(); }}
                  />
                  <div 
                    className="absolute bottom-0 right-0 translate-x-1/2 translate-y-1/2 w-3 h-3 bg-primary rounded-full cursor-se-resize opacity-0 group-hover:opacity-100 hover:!opacity-100"
                    onPointerDown={(e) => { e.stopPropagation(); startTextResize(e, field, 'se'); }}
                    onPointerMove={(e) => { e.stopPropagation(); handleTextResize(e); }}
                    onPointerUp={(e) => { e.stopPropagation(); stopTextResize(); }}
                  />
                </div>
              ) : (
                <div className="relative w-full h-full border border-dashed border-transparent hover:border-primary/40 rounded transition-colors">
                  <div
                    className="px-1 py-0.5 whitespace-pre-wrap break-words h-full overflow-hidden"
                    style={{
                      fontSize: field.fontSize,
                      color: field.color,
                    }}
                  >
                    {field.text}
                  </div>
                  
                  {/* Resize handles for non-editing mode (visible on hover) */}
                  {/* Corner resize handle (bottom-right) - most commonly used */}
                  <div 
                    className="absolute bottom-0 right-0 translate-x-1/2 translate-y-1/2 w-3 h-3 bg-primary/70 rounded-full cursor-se-resize opacity-0 group-hover:opacity-100 hover:!opacity-100 transition-opacity"
                    onPointerDown={(e) => { e.stopPropagation(); startTextResize(e, field, 'se'); }}
                    onPointerMove={(e) => { e.stopPropagation(); handleTextResize(e); }}
                    onPointerUp={(e) => { e.stopPropagation(); stopTextResize(); }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Word Count Footer */}
      {showWordCount && editable && (
        <div className="px-3 py-1.5 border-t text-xs text-muted-foreground flex items-center justify-between">
          <span>{stats.words} Wörter</span>
          <span>{stats.chars} Zeichen</span>
        </div>
      )}
    </div>
  );

  return editorContent;
}

// Styles for the editor elements
export const richTextEditorStyles = `
  .task-list {
    list-style: none;
    padding-left: 0;
    margin: 0;
  }
  
  .task-item {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin: 0.25rem 0;
  }
  
  .task-item > label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    user-select: none;
  }
  
  .task-item > label > input[type="checkbox"] {
    width: 1rem;
    height: 1rem;
    accent-color: hsl(var(--primary));
    cursor: pointer;
  }
  
  .task-item[data-checked="true"] > div {
    text-decoration: line-through;
    color: hsl(var(--muted-foreground));
  }
  
  .task-item > div {
    flex: 1;
  }

  /* Table styles - Fixed display */
  .ProseMirror table,
  .ProseMirror .editor-table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    margin: 1rem 0;
    overflow: hidden;
  }
  
  .ProseMirror th,
  .ProseMirror td {
    border: 2px solid hsl(var(--border));
    padding: 0.5rem 0.75rem;
    min-width: 80px;
    vertical-align: top;
    position: relative;
    box-sizing: border-box;
  }
  
  .ProseMirror th {
    background-color: hsl(var(--muted));
    font-weight: 600;
    text-align: left;
  }
  
  .ProseMirror td {
    background-color: hsl(var(--background));
  }
  
  .ProseMirror .selectedCell:after {
    z-index: 2;
    position: absolute;
    content: "";
    left: 0; right: 0; top: 0; bottom: 0;
    background: hsl(var(--primary) / 0.1);
    pointer-events: none;
  }
  
  .ProseMirror .column-resize-handle {
    position: absolute;
    right: -2px;
    top: 0;
    bottom: -2px;
    width: 4px;
    background-color: hsl(var(--primary));
    pointer-events: none;
  }

  .ProseMirror.resize-cursor {
    cursor: col-resize;
  }

  /* Blockquote styles */
  .ProseMirror blockquote {
    border-left: 3px solid hsl(var(--primary));
    padding-left: 1rem;
    margin: 0.5rem 0;
    color: hsl(var(--muted-foreground));
    font-style: italic;
  }

  /* Code block styles */
  .ProseMirror pre {
    background: hsl(var(--muted));
    border-radius: 0.375rem;
    padding: 0.75rem 1rem;
    font-family: ui-monospace, monospace;
    font-size: 0.875rem;
    overflow-x: auto;
  }

  .ProseMirror code {
    background: hsl(var(--muted));
    padding: 0.125rem 0.25rem;
    border-radius: 0.25rem;
    font-family: ui-monospace, monospace;
    font-size: 0.875em;
  }

  /* Image styles */
  .ProseMirror img {
    max-width: 100%;
    height: auto;
    border-radius: 0.375rem;
  }

  /* Highlight styles */
  .ProseMirror mark {
    border-radius: 0.125rem;
    padding: 0.125rem 0;
  }

  /* Superscript & Subscript */
  .ProseMirror sup {
    font-size: 0.75em;
    vertical-align: super;
  }

  .ProseMirror sub {
    font-size: 0.75em;
    vertical-align: sub;
  }

  /* Nested list indentation */
  .ProseMirror ul ul,
  .ProseMirror ol ol,
  .ProseMirror ul ol,
  .ProseMirror ol ul {
    margin-left: 1.5rem;
    margin-top: 0.25rem;
    margin-bottom: 0.25rem;
  }

  .ProseMirror ul ul ul,
  .ProseMirror ol ol ol {
    margin-left: 1.5rem;
  }

  /* Different list markers for nested levels */
  .ProseMirror ul {
    list-style-type: disc;
  }

  .ProseMirror ul ul {
    list-style-type: circle;
  }

  .ProseMirror ul ul ul {
    list-style-type: square;
  }
`;

// Keep backward compatibility
export const taskListStyles = richTextEditorStyles;
